import { IconType } from "react-icons";
import {
  LuBell,
  LuFolderOpen,
  LuHouse,
  LuLogOut,
  LuMessagesSquare,
  LuUser,
} from "react-icons/lu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { BsPeople } from "react-icons/bs";
import { TelegramConnectCard } from "./TelegramConnectCard";
import { fetchNotifications } from "../../lib/notifications";
import { AppNotification } from "../../models/notification";

type NavItem = { icon: IconType; label: string; to: string; };

const partnerItems: NavItem[] = [
  { icon: LuHouse, label: "Головна", to: "/dashboard" },
  { icon: LuFolderOpen, label: "Мої проєкти", to: "/projects"},
  { icon: LuBell, label: "Сповіщення", to: "/notifications" },
  { icon: LuUser, label: "Профіль", to: "/profile" },
];

const staffItems: NavItem[] = [
  { icon: LuHouse, label: "Головна", to: "/dashboard" },
  { icon: LuFolderOpen, label: "Проєкти", to: "/admin/projects" },
  { icon: BsPeople, label: "Партнери", to: "/admin/partners" },
  { icon: LuMessagesSquare, label: "Повідомлення", to: "/admin/messages" },
  { icon: LuUser, label: "Профіль", to: "/profile" },
];

export const Sidebar = () => {
  const { user, profile, role, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const items = role ? (role === "admin" || role === "manager") ? staffItems : partnerItems : [];

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (role === "admin" || role === "manager") return;
    let cancelled = false;
    fetchNotifications(null)
      .then((n: AppNotification[]) => {
        if (!cancelled) setUnreadCount(n.filter((x) => x.read_at === null).length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [role, pathname]);

  useEffect(() => {
    const onReadAll = () => setUnreadCount(0);
    window.addEventListener("notifications:read-all", onReadAll);
    return () => window.removeEventListener("notifications:read-all", onReadAll);
  }, []);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col overflow-y-auto border-r border-line bg-night px-4 pb-5 pt-8 md:flex">
        <nav className="flex flex-col gap-1.5">
          {items.map(({ icon: Icon, label, to }) => {
            const isActive = pathname.startsWith(to);
            const badge = to === "/notifications" && unreadCount > 0 ? unreadCount : null;
            return (
              <Link
                key={label}
                to={to}
                className={`flex items-center gap-3 rounded px-4 py-3 text-left text-sm font-medium transition ${
                  isActive
                    ? "bg-brand/10 text-brand"
                    : "text-neutral-300   hover:text-white"
                }`}
              >
                <Icon className="text-lg" aria-hidden />
                {label}
                {badge !== null && (
                  <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-semibold text-black">
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col gap-5 pt-8">
          <TelegramConnectCard />
          <div className="flex justify-between gap-1 rounded-lg bg-white/5 px-4 py-3">
            <div className="min-w-0 text-sm">
              <p className="truncate font-semibold text-white">
                {profile?.name}
              </p>
              <p className="truncate text-xs text-neutral-400">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="font-medium text-neutral-300 transition hover:text-red-500"
            >
              <LuLogOut aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile bottom navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-white/10 bg-night/80 px-2 pb-[env(safe-area-inset-bottom)] backdrop-blur-lg md:hidden">
        {items.map(({ icon: Icon, label, to }) => {
          const isActive = pathname.startsWith(to);
          const badge = to === "/notifications" && unreadCount > 0 ? unreadCount : null;
          return (
            <Link
              key={label}
              to={to}
              className={`flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[11px] leading-5 whitespace-nowrap text-center font-medium transition ${
                isActive ? "text-brand" : "text-neutral-400"
              }`}
            >
              <div className="relative">
                <Icon className="text-xl" aria-hidden />
                {badge !== null && (
                  <span className="absolute -right-3 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-black">
                    {badge}
                  </span>
                )}
              </div>
              {label}
            </Link>
          );
        })}
      </nav>
    </>
  );
};
